/**
 * ATTOM Property Data Page
 *
 * Platform-admin console for the ATTOM property-data provider: configure the API
 * key (Configuration tab), validate it, view health, and run test lookups.
 * Allows property lookups, viewing valuations, assessments, and sales history.
 *
 * A failed health read is said in words. The cards printed "..." for a failed
 * read, the same glyph as loading, beside a red error icon, and the Health
 * Details tab presented "Configured: No", "Last Success: Never" and "0ms" when
 * nothing had been read at all (op-g2-3, op-g2-4).
 *
 * A failed request prints what could not be read first and the server's
 * detail after it, trimmed of the bare colon an HTTP/2 response leaves
 * ("HTTP 404:"), through lib/read-failure (op-g2-1).
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  Badge,
  Checkbox,
  Heading,
  BodyText,
  Input,
  MetricCard,
  METRIC_READ_CAPTIONS,
  PageHeader,
  Skeleton,
  Table,
  Tabs,
} from '@aaimplatform/ui';
import type { TabItem, TableColumn } from '@aaimplatform/ui';
import {
  RefreshCw as RefreshIcon,
  Search as SearchIcon,
  Home as PropertyIcon,
  CheckCircle2 as SuccessIcon,
  AlertCircle as ErrorIcon,
  Info as InfoIcon,
  Gauge as LatencyIcon,
  Shield as ShieldIcon,
  Settings as SettingsIcon,
} from 'lucide-react';
import { attomTestApi, providerCredentialApi } from '../api/client';
import type {
  AttomAddress,
  AttomHealthStatus,
  AttomTestLookupResponse,
  SampleAddress,
  ProviderCredential,
} from '../api/client';
import {
  ABSENT,
  formatCount,
  formatDate,
  formatDateTime,
  formatMoney,
  formatRatioAsPercent,
} from '../utils/display-format';
import { enumLabel } from '../utils/status-labels';
import { readFailureDetail } from '../lib/read-failure';

type TabType = 'lookup' | 'results' | 'health' | 'configure';

const TABS_ID = 'attom-tabs';

const TABS: TabItem[] = [
  { id: 'lookup', label: 'Property Lookup' },
  { id: 'results', label: 'Results' },
  { id: 'health', label: 'Health Details' },
  { id: 'configure', label: 'Configuration' },
];

type CircuitBreakerState = AttomHealthStatus['circuitBreakerState'];

export const CIRCUIT_BREAKER_LABEL: Record<CircuitBreakerState, string> = {
  closed: 'Closed',
  half_open: 'Half-open',
  open: 'Open',
};

/** What the health cards and the Health Details tab say when no health was read. */
export const HEALTH_UNAVAILABLE = 'Health data unavailable';

type SaleEvent = NonNullable<AttomTestLookupResponse['salesHistory']>[number];

function isMeasured(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function formatLatency(value: number | null | undefined): string {
  return isMeasured(value) ? `${formatCount(Math.round(value))} ms` : ABSENT;
}

function formatArea(value: number | null | undefined): string {
  return isMeasured(value) ? `${formatCount(value)} sq ft` : ABSENT;
}

function formatWhole(value: number | null | undefined): string {
  return isMeasured(value) ? String(value) : ABSENT;
}

const SALES_COLUMNS: TableColumn<SaleEvent>[] = [
  {
    key: 'eventDate',
    label: 'Date',
    render: (value: string | undefined) => formatDate(value),
  },
  {
    key: 'eventType',
    label: 'Type',
    render: (value: string | undefined) =>
      value ? <Badge variant="neutral">{value}</Badge> : ABSENT,
  },
  {
    key: 'value',
    label: 'Amount',
    align: 'right',
    render: (value: number | undefined) => formatMoney(value),
  },
  {
    key: 'description',
    label: 'Description',
    render: (value: string | undefined) => value || ABSENT,
  },
];

const LABEL_CLASS = 'block text-sm font-medium text-secondary mb-1';

/**
 * The five health tiles. Each state reads differently: loading shows a
 * skeleton, a failed read shows "--" with the failed caption, and only a
 * delivered payload shows figures.
 */
export function AttomHealthCards({
  health,
  loading,
  failed,
}: {
  health: AttomHealthStatus | null;
  loading: boolean;
  failed: boolean;
}) {
  if (loading && !health) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
            <Skeleton className="h-3 w-24 mb-3" />
            <Skeleton className="h-8 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (!health) {
    const caption = failed ? METRIC_READ_CAPTIONS.failed : METRIC_READ_CAPTIONS.unread;
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <MetricCard label="Status" value={ABSENT} caption={caption} />
        <MetricCard label="Circuit Breaker" value={ABSENT} caption={caption} />
        <MetricCard label="Requests (5 min)" value={ABSENT} caption={caption} />
        <MetricCard label="Errors (5 min)" value={ABSENT} caption={caption} />
        <MetricCard label="Avg Latency" value={ABSENT} caption={caption} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      <MetricCard
        label="Status"
        value={health.isHealthy ? 'Healthy' : 'Unhealthy'}
        valueColor={health.isHealthy ? 'growth' : 'accent'}
      />
      <MetricCard
        label="Circuit Breaker"
        value={enumLabel(CIRCUIT_BREAKER_LABEL, health.circuitBreakerState)}
        valueColor={health.circuitBreakerState === 'open' ? 'accent' : undefined}
      />
      <MetricCard label="Requests (5 min)" value={formatCount(health.requestsLast5Min)} />
      <MetricCard
        label="Errors (5 min)"
        value={formatCount(health.errorsLast5Min)}
        valueColor={isMeasured(health.errorsLast5Min) && health.errorsLast5Min > 0 ? 'accent' : undefined}
      />
      <MetricCard label="Avg Latency" value={formatLatency(health.avgLatencyMs)} />
    </div>
  );
}

/** The Health Details tab. With no payload it says so once, instead of defaults. */
export function AttomHealthDetails({
  health,
  healthError,
  validating,
  validateMessage,
  onValidate,
}: {
  health: AttomHealthStatus | null;
  healthError: string | null;
  validating: boolean;
  validateMessage: { type: 'success' | 'error'; text: string } | null;
  onValidate: () => void;
}) {
  if (!health) {
    return (
      <Card className="p-6">
        <Heading level={3} size="card" className="mb-2">{HEALTH_UNAVAILABLE}</Heading>
        <BodyText variant="ui" className="text-sm text-tertiary">
          {healthError
            ? `The ATTOM health read failed. Server response: ${readFailureDetail(healthError)}`
            : 'The ATTOM health read has not returned yet.'}
        </BodyText>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6">
        <Heading level={3} size="card" className="mb-4 flex items-center gap-2">
          <ShieldIcon size={20} />
          API Key Status
        </Heading>
        <dl className="space-y-3">
          <div className="flex justify-between items-center">
            <dt className="text-tertiary">Configured:</dt>
            <dd>
              <Badge variant={health.apiKeyConfigured ? 'success' : 'error'}>
                {health.apiKeyConfigured ? 'Yes' : 'No'}
              </Badge>
            </dd>
          </div>
          <div className="flex justify-between items-center">
            <dt className="text-tertiary">Validated:</dt>
            <dd>
              <Badge variant={health.apiKeyValid ? 'success' : 'warning'}>
                {health.apiKeyValid ? 'Valid' : 'Not validated'}
              </Badge>
            </dd>
          </div>
        </dl>
        {validateMessage && (
          <Alert type={validateMessage.type} className="mt-4">
            {validateMessage.text}
          </Alert>
        )}
        <Button variant="secondary" onClick={onValidate} disabled={validating} className="mt-4">
          {validating ? 'Validating...' : 'Validate API Key'}
        </Button>
      </Card>

      <Card className="p-6">
        <Heading level={3} size="card" className="mb-4 flex items-center gap-2">
          <LatencyIcon size={20} />
          Performance
        </Heading>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-tertiary">Last Success:</dt>
            <dd className="text-sm">
              {health.lastSuccessfulRequest ? formatDateTime(health.lastSuccessfulRequest) : 'None recorded'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-tertiary">Last Failure:</dt>
            <dd className="text-sm">
              {health.lastFailedRequest ? formatDateTime(health.lastFailedRequest) : 'None recorded'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-tertiary">Avg Latency:</dt>
            <dd className="font-semibold">{formatLatency(health.avgLatencyMs)}</dd>
          </div>
        </dl>
      </Card>

      <Card className="p-6 lg:col-span-2">
        <Heading level={3} size="card" className="mb-4">Configuration</Heading>
        {health.config ? (
          <dl className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <dt className="text-sm text-tertiary">Base URL</dt>
              <dd className="text-sm font-mono truncate">{health.config.baseUrl ?? ABSENT}</dd>
            </div>
            <div>
              <dt className="text-sm text-tertiary">Timeout</dt>
              <dd className="text-sm">{isMeasured(health.config.timeout) ? `${formatCount(health.config.timeout)} ms` : ABSENT}</dd>
            </div>
            <div>
              <dt className="text-sm text-tertiary">Max Retries</dt>
              <dd className="text-sm">{formatWhole(health.config.maxRetries)}</dd>
            </div>
            <div>
              <dt className="text-sm text-tertiary">Rate Limit/sec</dt>
              <dd className="text-sm">{formatWhole(health.config.rateLimitPerSecond)}</dd>
            </div>
            <div>
              <dt className="text-sm text-tertiary">Rate Limit/min</dt>
              <dd className="text-sm">{formatWhole(health.config.rateLimitPerMinute)}</dd>
            </div>
          </dl>
        ) : (
          <BodyText variant="ui" className="text-sm text-tertiary">
            The health payload carries no adapter configuration.
          </BodyText>
        )}
      </Card>
    </div>
  );
}

export function AttomTestInterfacePage() {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('lookup');

  // Form state
  const [address, setAddress] = useState<AttomAddress>({
    street: '',
    city: '',
    state: '',
    zip: '',
  });
  const [includeAssessment, setIncludeAssessment] = useState(true);
  const [includeSchools, setIncludeSchools] = useState(false);
  const [includeHistory, setIncludeHistory] = useState(true);

  // Results state
  const [lookupResult, setLookupResult] = useState<AttomTestLookupResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Health state
  const [healthData, setHealthData] = useState<AttomHealthStatus | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [validateMessage, setValidateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sample addresses
  const [sampleAddresses, setSampleAddresses] = useState<SampleAddress[]>([]);
  const [samplesLoaded, setSamplesLoaded] = useState(false);
  const [samplesError, setSamplesError] = useState<string | null>(null);

  // Configure tab state
  const [credential, setCredential] = useState<ProviderCredential | null>(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [configMessage, setConfigMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch health status
  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const data = await attomTestApi.getHealth();
      setHealthData(data);
      setHealthError(null);
    } catch (err) {
      setHealthData(null);
      setHealthError(readFailureDetail(err));
    } finally {
      setHealthLoading(false);
    }
  }, []);

  // Fetch sample addresses
  const fetchSampleAddresses = useCallback(async () => {
    try {
      const data = await attomTestApi.getSampleAddresses();
      setSampleAddresses(data.sampleAddresses);
      setSamplesError(null);
    } catch (err) {
      setSamplesError(readFailureDetail(err));
    } finally {
      setSamplesLoaded(true);
    }
  }, []);

  // Fetch ATTOM credential
  const fetchCredential = useCallback(async () => {
    try {
      const data = await providerCredentialApi.getCredential('ATTOM');
      setCredential(data);
    } catch {
      // Credential may not exist yet
      setCredential(null);
    }
  }, []);

  // Save updated API key
  const handleSaveApiKey = async () => {
    if (!newApiKey.trim()) return;
    setConfigLoading(true);
    setConfigMessage(null);
    try {
      const updated = await providerCredentialApi.updateCredential('ATTOM', newApiKey.trim());
      setCredential(updated);
      setNewApiKey('');
      setConfigMessage({ type: 'success', text: 'API key updated. Health check refreshing.' });
      fetchHealth();
    } catch (err) {
      setConfigMessage({
        type: 'error',
        text: `The API key was not saved. Server response: ${readFailureDetail(err)}`,
      });
    } finally {
      setConfigLoading(false);
    }
  };

  // Load sample addresses on mount
  useEffect(() => {
    fetchSampleAddresses();
    fetchHealth();
  }, [fetchSampleAddresses, fetchHealth]);

  // Perform property lookup
  const handleLookup = async () => {
    if (!address.street || !address.city || !address.state || !address.zip) {
      setError('Please fill in all address fields');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await attomTestApi.testLookup({
        address,
        includeAssessment,
        includeSchools,
        includeHistory,
      });
      setLookupResult(result);
      setActiveTab('results');
    } catch (err) {
      setError(`The lookup failed. Server response: ${readFailureDetail(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Use sample address
  const useSampleAddress = (sample: SampleAddress) => {
    setAddress(sample.address);
  };

  // Validate API key. The result is shown inline beside the button, where
  // window.alert used to report it.
  const handleValidateKey = async () => {
    setValidating(true);
    setValidateMessage(null);
    try {
      const result = await attomTestApi.validateApiKey();
      await fetchHealth();
      // valid: false is a completed validation that rejected the key.
      if (result.valid === false) {
        setValidateMessage({ type: 'error', text: result.message ?? 'The API key did not validate.' });
      } else if (result.valid === true) {
        setValidateMessage({ type: 'success', text: result.message ?? 'The API key validated.' });
      } else {
        setValidateMessage({
          type: 'error',
          text: result.message ?? 'The validation response did not say whether the key is valid.',
        });
      }
    } catch (err) {
      setValidateMessage({
        type: 'error',
        text: `The validation did not complete. Server response: ${readFailureDetail(err)}`,
      });
    } finally {
      setValidating(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as TabType);
    if (tab === 'configure') fetchCredential();
  };

  return (
    <div>
      <PageHeader
        description="Configure the ATTOM property-data provider: API key, validation, health, and test lookups"
        actions={
          <Button
            variant="secondary"
            icon={RefreshIcon}
            iconPosition="left"
            onClick={fetchHealth}
            disabled={healthLoading}
          >
            Refresh Health
          </Button>
        }
      />

      {healthError && !healthData && (
        <Alert type="error" title="ATTOM health could not be read" className="mb-6">
          {`No health figures are shown. Server response: ${healthError}`}
        </Alert>
      )}

      {/* Health Summary Cards */}
      <AttomHealthCards health={healthData} loading={healthLoading} failed={!!healthError} />

      {/* Tabs */}
      <Tabs
        id={TABS_ID}
        aria-label="ATTOM provider"
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        className="mb-4"
        panelClassName="focus:outline-none"
      >
        {activeTab === 'lookup' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lookup Form */}
            <Card className="lg:col-span-2 p-6">
              <Heading level={3} size="card" className="mb-4 flex items-center gap-2">
                <PropertyIcon size={20} />
                Property Address
              </Heading>

              {error && <Alert type="error" className="mb-4">{error}</Alert>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label htmlFor="attom-street" className={LABEL_CLASS}>
                    Street Address
                  </label>
                  <Input
                    id="attom-street"
                    type="text"
                    value={address.street}
                    onChange={(e) => setAddress({ ...address, street: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                <div>
                  <label htmlFor="attom-city" className={LABEL_CLASS}>
                    City
                  </label>
                  <Input
                    id="attom-city"
                    type="text"
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    placeholder="San Francisco"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="attom-state" className={LABEL_CLASS}>
                      State
                    </label>
                    <Input
                      id="attom-state"
                      type="text"
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="CA"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label htmlFor="attom-zip" className={LABEL_CLASS}>
                      ZIP Code
                    </label>
                    <Input
                      id="attom-zip"
                      type="text"
                      inputMode="numeric"
                      value={address.zip}
                      onChange={(e) => setAddress({ ...address, zip: e.target.value.replace(/[^0-9-]/g, '').slice(0, 10) })}
                      placeholder="94102"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mb-6">
                <Checkbox
                  label="Include Assessment"
                  checked={includeAssessment}
                  onChange={(checked) => setIncludeAssessment(checked)}
                />
                <Checkbox
                  label="Include Schools"
                  checked={includeSchools}
                  onChange={(checked) => setIncludeSchools(checked)}
                />
                <Checkbox
                  label="Include Sales History"
                  checked={includeHistory}
                  onChange={(checked) => setIncludeHistory(checked)}
                />
              </div>

              <Button
                variant="primary"
                icon={isLoading ? RefreshIcon : SearchIcon}
                iconPosition="left"
                onClick={handleLookup}
                disabled={isLoading}
                className="w-full md:w-auto"
              >
                {isLoading ? 'Looking up...' : 'Lookup Property'}
              </Button>
            </Card>

            {/* Sample Addresses */}
            <Card className="p-6">
              <Heading level={3} size="card" className="mb-4">Sample Addresses</Heading>
              <BodyText variant="ui" className="text-sm text-tertiary mb-4">
                Click to use a sample address for testing
              </BodyText>
              {samplesError ? (
                <BodyText variant="ui" className="text-sm text-secondary">
                  {`Sample addresses could not be read. Server response: ${samplesError}`}
                </BodyText>
              ) : samplesLoaded && sampleAddresses.length === 0 ? (
                <BodyText variant="ui" className="text-sm text-secondary">
                  The provider returned no sample addresses.
                </BodyText>
              ) : (
                <div className="space-y-2">
                  {sampleAddresses.map((sample, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => useSampleAddress(sample)}
                      className="w-full text-left p-3 bg-surface-secondary rounded-md hover:bg-surface-tertiary transition-colors"
                    >
                      <BodyText variant="ui" className="font-medium text-sm">{sample.label}</BodyText>
                      <BodyText variant="ui" className="text-xs text-tertiary">
                        {sample.address.street}, {sample.address.city}, {sample.address.state} {sample.address.zip}
                      </BodyText>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'results' && (
          <div>
            {!lookupResult ? (
              <Card className="p-6 text-center">
                <InfoIcon className="text-tertiary mb-2 mx-auto" size={32} />
                <BodyText variant="ui" className="text-tertiary">No lookup results yet. Use the Property Lookup tab to search for a property.</BodyText>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <Card className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <Heading level={3} size="card">Lookup Results</Heading>
                      <BodyText variant="ui" className="text-sm text-tertiary">
                        {lookupResult.address.street}, {lookupResult.address.city}, {lookupResult.address.state} {lookupResult.address.zip}
                      </BodyText>
                    </div>
                    <div className="text-right">
                      <BodyText variant="ui" className="text-sm text-tertiary">Processing Time</BodyText>
                      <BodyText variant="ui" className="font-semibold">{formatLatency(lookupResult.processingTimeMs)}</BodyText>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <PartStatus label="Profile" succeeded={lookupResult.profileSuccess} />
                    {includeAssessment && <PartStatus label="Assessment" succeeded={lookupResult.assessmentSuccess} />}
                    {includeSchools && <PartStatus label="Schools" succeeded={lookupResult.schoolsSuccess} />}
                    {includeHistory && <PartStatus label="History" succeeded={lookupResult.historySuccess} />}
                  </div>
                </Card>

                {/* Property Profile */}
                {lookupResult.profile && (
                  <Card className="p-6">
                    <Heading level={3} size="card" className="mb-4">Property Profile</Heading>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <BodyText variant="ui" className="text-sm font-medium text-tertiary mb-2">Identifiers</BodyText>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-tertiary">ATTOM ID:</dt>
                            <dd className="font-mono">{lookupResult.profile.attomId}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">FIPS:</dt>
                            <dd className="font-mono">{lookupResult.profile.fips}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">APN:</dt>
                            <dd className="font-mono">{lookupResult.profile.apn}</dd>
                          </div>
                        </dl>
                      </div>

                      <div>
                        <BodyText variant="ui" className="text-sm font-medium text-tertiary mb-2">Characteristics</BodyText>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Type:</dt>
                            <dd>{lookupResult.profile.characteristics.propertyType || ABSENT}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Year Built:</dt>
                            <dd>{formatWhole(lookupResult.profile.characteristics.yearBuilt)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Living Size:</dt>
                            <dd>{formatArea(lookupResult.profile.characteristics.livingSize)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Beds/Baths:</dt>
                            <dd>
                              {formatWhole(lookupResult.profile.characteristics.bedrooms)} /{' '}
                              {formatWhole(lookupResult.profile.characteristics.bathrooms)}
                            </dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Lot Size:</dt>
                            <dd>{formatArea(lookupResult.profile.characteristics.lotSize)}</dd>
                          </div>
                        </dl>
                      </div>

                      <div>
                        <BodyText variant="ui" className="text-sm font-medium text-tertiary mb-2">Valuation</BodyText>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-tertiary">AVM:</dt>
                            <dd className="font-semibold text-primary">{formatMoney(lookupResult.profile.valuation.avm)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Confidence:</dt>
                            <dd>{formatRatioAsPercent(lookupResult.profile.valuation.confidenceScore)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Assessed:</dt>
                            <dd>{formatMoney(lookupResult.profile.valuation.assessedValue)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Tax Amount:</dt>
                            <dd>{formatMoney(lookupResult.profile.valuation.taxAmount)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-tertiary">Last Sale:</dt>
                            <dd>{formatMoney(lookupResult.profile.lastSale.amount)}</dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Sales History */}
                {lookupResult.salesHistory && lookupResult.salesHistory.length > 0 && (
                  <Card className="p-6">
                    <Heading level={3} size="card" className="mb-4">Sales History</Heading>
                    <Table<SaleEvent> columns={SALES_COLUMNS} data={lookupResult.salesHistory} label="Sales history" />
                  </Card>
                )}

                {/* Raw Response */}
                <Card className="p-6">
                  <Heading level={3} size="card" className="mb-4">Raw API Response</Heading>
                  <pre className="bg-surface-secondary p-4 rounded-md overflow-x-auto text-xs">
                    {JSON.stringify(lookupResult, null, 2)}
                  </pre>
                </Card>
              </div>
            )}
          </div>
        )}

        {activeTab === 'health' && (
          <AttomHealthDetails
            health={healthData}
            healthError={healthError}
            validating={validating}
            validateMessage={validateMessage}
            onValidate={handleValidateKey}
          />
        )}

        {activeTab === 'configure' && (
          <div className="max-w-xl">
            <Card className="p-6">
              <Heading level={3} size="card" className="mb-4 flex items-center gap-2">
                <SettingsIcon size={20} />
                ATTOM API Key
              </Heading>

              {credential && (
                <div className="mb-6">
                  <dl className="space-y-3">
                    <div className="flex justify-between items-center">
                      <dt className="text-tertiary">Current Key:</dt>
                      <dd className="font-mono text-sm">{credential.maskedKey}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-tertiary">Status:</dt>
                      <dd>
                        <Badge variant={credential.isActive ? 'success' : 'error'}>
                          {credential.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-tertiary">Last Updated:</dt>
                      <dd className="text-sm">{formatDateTime(credential.lastUpdatedAt)}</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-tertiary">Updated By:</dt>
                      <dd className="text-sm">{credential.lastUpdatedBy}</dd>
                    </div>
                  </dl>
                </div>
              )}

              {!credential && (
                <BodyText variant="ui" className="text-tertiary text-sm mb-4">
                  No ATTOM credential stored. Enter an API key below to configure.
                </BodyText>
              )}

              {configMessage && (
                <Alert type={configMessage.type} className="mb-4">
                  {configMessage.text}
                </Alert>
              )}

              <div className="space-y-3">
                <div>
                  <label htmlFor="attom-new-api-key" className={LABEL_CLASS}>
                    New API Key
                  </label>
                  <Input
                    id="attom-new-api-key"
                    type="password"
                    value={newApiKey}
                    onChange={(e) => setNewApiKey(e.target.value)}
                    placeholder="Enter new ATTOM API key"
                    autoComplete="off"
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={handleSaveApiKey}
                  disabled={configLoading || !newApiKey.trim()}
                >
                  {configLoading ? 'Saving...' : 'Update API Key'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </Tabs>
    </div>
  );
}

/** Whether one part of a lookup returned. Absent reads as not reported, never as failed. */
function PartStatus({ label, succeeded }: { label: string; succeeded: boolean | undefined }) {
  return (
    <div className="flex items-center gap-2">
      {succeeded === true ? (
        <SuccessIcon className="text-growth" size={20} aria-hidden />
      ) : succeeded === false ? (
        <ErrorIcon className="text-error" size={20} aria-hidden />
      ) : (
        <InfoIcon className="text-tertiary" size={20} aria-hidden />
      )}
      <span className="text-sm">
        {label}
        <span className="sr-only">
          {succeeded === true ? ': returned' : succeeded === false ? ': failed' : ': not reported'}
        </span>
      </span>
    </div>
  );
}
